/**
 * Accounting Store - UI state only.
 *
 * All server data (accounts, movements, invoices, chart of accounts) lives
 * in the GraphQL backend and is read via the react-query hooks in
 * `hooks/useAccounting.ts`. This store keeps only the UI state that
 * benefits from localStorage persistence:
 *   - active sidebar filter key
 *   - selected entity IDs (account, invoice)
 *   - sidebar menu definition (constant; not persisted)
 *
 * What was removed in this commit:
 *   - Business arrays: accounts, movements, invoices, chartOfAccounts
 *   - Transitional load/cache state: status, error, initialized
 *   - Transitional CRUD: createMovement, updateMovement, createInvoice,
 *     updateInvoice, deleteInvoice (no consumer in shared/apps; mutations
 *     live in `hooks/useAccounting.ts` as react-query hooks)
 *   - loadFromServer (only consumer was useAppInit; react-query fetches
 *     on first mount now)
 *   - aiDraftInvoice + setAiDraftInvoice (no consumer anywhere)
 *
 * Why split: persisting server data to localStorage duplicates the source
 * of truth, hides write failures, and breaks the multi-tenant model. UI
 * state stays local; everything else is fetched fresh on each session.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createZustandStorage } from '../storage/adapter';
import type { SidebarMenu } from '../domain/models';

// =============================================================================
// SIDEBAR MENU CONFIG
// =============================================================================

const ACCOUNTING_SIDEBAR_MENU: SidebarMenu = {
  sections: [
    {
      title: 'Vista',
      items: [
        { key: 'all', label: 'Tutti', hint: 'Tutti i movimenti' },
        { key: 'in', label: 'Entrate', hint: 'Solo incassi' },
        { key: 'out', label: 'Uscite', hint: 'Solo pagamenti' },
        { key: 'unreconciled', label: 'Non riconciliati', hint: 'Da verificare' },
      ],
    },
    {
      title: 'Fatture',
      items: [
        { key: 'invoices-issued', label: 'Emesse', hint: 'Fatture clienti' },
        { key: 'invoices-received', label: 'Ricevute', hint: 'Fatture fornitori' },
        { key: 'invoices-overdue', label: 'Scadute', hint: 'Da sollecitare' },
        { key: 'invoices-paid', label: 'Pagate', hint: 'Archiviate' },
      ],
    },
  ],
};

// =============================================================================
// STORE STATE
// =============================================================================

interface AccountingState {
  // UI state
  activeFilterKey: string;
  selectedAccountId: string | null;
  selectedInvoiceId: string | null;
  sidebarMenu: SidebarMenu;

  // Actions - View
  setActiveFilterKey: (key: string) => void;
  selectAccount: (id: string | null) => void;
  selectInvoice: (id: string | null) => void;
}

// =============================================================================
// STORE
// =============================================================================

export const useAccountingStore = create<AccountingState>()(
  persist(
    (set) => ({
      // UI state
      activeFilterKey: 'all',
      selectedAccountId: null,
      selectedInvoiceId: null,
      sidebarMenu: ACCOUNTING_SIDEBAR_MENU,

      // View actions
      setActiveFilterKey: (key) => set({ activeFilterKey: key }),
      selectAccount: (id) => set({ selectedAccountId: id }),
      selectInvoice: (id) => set({ selectedInvoiceId: id }),
    }),
    {
      name: 'accounting-store',
      storage: createZustandStorage(),
      partialize: (state) => ({
        activeFilterKey: state.activeFilterKey,
        selectedAccountId: state.selectedAccountId,
        selectedInvoiceId: state.selectedInvoiceId,
      }),
    }
  )
);
